/**
 * Normalises a user-entered link URL so that bare domains become absolute
 * external links instead of being treated as relative paths by the browser.
 *
 * Rich-text editors (Quill) store a link exactly as typed, so a value like
 * `google.com` ends up as `<a href="google.com">` and resolves relative to
 * the current page (e.g. `/news/google.com`). Prepending a scheme makes it an
 * external link.
 *
 * Left untouched:
 *  - values that already have a scheme (`https:`, `http:`, `mailto:`, `tel:`, ...)
 *  - protocol-relative URLs (`//cdn.example.com`)
 *  - root-relative / in-page links (`/products`, `#section`, `?q=1`) — these are
 *    intentional internal links
 */
export function normalizeLinkUrl(url: string): string {
  if (!url) return url;

  const trimmed = url.trim();

  // Already has a URI scheme (RFC 3986: scheme = ALPHA *( ALPHA / DIGIT / "+" / "-" / "." ))
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;

  // Protocol-relative, root-relative, in-page anchor or query-only — leave as-is
  if (/^(\/\/|\/|#|\?)/.test(trimmed)) return trimmed;

  // Otherwise treat as a bare external domain/path and default to https
  return `https://${trimmed}`;
}
